class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegate from the stable <cart-drawer> host so validation survives
    // add-to-cart / section re-renders that replace form and checkout nodes.
    this.addEventListener('click', this.onCheckoutClick.bind(this));
    this.addEventListener('submit', this.onCheckoutSubmit.bind(this));
  }

  isCheckoutTrigger(element) {
    return Boolean(
      element &&
        this.contains(element) &&
        (element.id === 'CartDrawer-Checkout' || element.getAttribute('name') === 'checkout')
    );
  }

  onCheckoutClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"]');
    if (!this.isCheckoutTrigger(checkoutButton)) return;
    if (!this.validateCheckboxes()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onCheckoutSubmit(event) {
    const submitter = event.submitter || this.querySelector('#CartDrawer-Checkout');
    if (!this.isCheckoutTrigger(submitter)) return;
    if (!this.validateCheckboxes()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  validateCheckboxes() {
    const waiverCheckbox = this.querySelector('#CartDrawer-Waiver');
    const sponsorshipCheckbox = this.querySelector('#CartDrawer-Sponsorship-Terms');

    // Debug: Check if checkboxes exist
    console.log('Waiver checkbox found:', !!waiverCheckbox);
    console.log('Sponsorship checkbox found:', !!sponsorshipCheckbox);

    let isValid = true;
    let missingCheckboxes = [];

    // Check waiver checkbox if it exists
    if (waiverCheckbox && !waiverCheckbox.checked) {
      isValid = false;
      missingCheckboxes.push('waiver terms');
      console.log('Waiver checkbox not checked');
    }

    // Check sponsorship checkbox if it exists
    if (sponsorshipCheckbox && !sponsorshipCheckbox.checked) {
      isValid = false;
      missingCheckboxes.push('sponsorship terms and conditions');
      console.log('Sponsorship checkbox not checked');
    }

    if (!isValid) {
      const message =
        missingCheckboxes.length === 1
          ? `Please agree to the ${missingCheckboxes[0]} before proceeding to checkout.`
          : `Please agree to the ${missingCheckboxes.join(' and ')} before proceeding to checkout.`;

      alert(message);

      // Focus the first unchecked checkbox
      if (waiverCheckbox && !waiverCheckbox.checked) {
        waiverCheckbox.focus();
      } else if (sponsorshipCheckbox && !sponsorshipCheckbox.checked) {
        sponsorshipCheckbox.focus();
      }
    } else {
      console.log('All checkboxes validated successfully');
    }

    return isValid;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.style.removeProperty('opacity');
    this.style.removeProperty('visibility');
    this.style.removeProperty('pointer-events');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'animate');
    /* Force teardown immediately — WebKit mobile can leave visibility transitions stuck so the dimmed layer stays */
    this.style.setProperty('opacity', '0');
    this.style.setProperty('visibility', 'hidden');
    this.style.setProperty('pointer-events', 'none');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:closed', { bubbles: true }));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
